import { gql, useMutation, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import {
  ItemResaleRaffleUserDetailFragmentDoc,
  ResaleRaffleDetailQuery,
  ResaleRaffleDetailQueryVariables,
} from '@/clients/utoniq-core/schema'
import { useInitialLoading } from '@/components/lib/initialLoading'
import { useMessage, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { setItemSku } from '@/core/sku'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, routerPages, generatePath } =
    useRouter<{ raffleId: string; itemId: string }>()
  const { raffleId, itemId } = router.query
  const { showError } = useMessage()

  const { data, loading, error } = useQuery<
    ResaleRaffleDetailQuery,
    ResaleRaffleDetailQueryVariables
  >(QUERY, {
    variables: {
      raffleId,
      itemId,
    },
  })

  const [cancelResaleItemRaffle, { loading: submitting }] = useMutation(
    CANCEL_RESALE_ITEM_RAFFLE
  )

  const itemResaleRaffleUser = useMemo(() => {
    if (!data?.itemResaleRaffleUser) {
      return null
    }
    return {
      ...data?.itemResaleRaffleUser,
      item: setItemSku(
        data?.itemResaleRaffleUser?.item,
        data?.itemResaleRaffleUser.skuId
      ),
    }
  }, [data?.itemResaleRaffleUser])
  return {
    state: {
      loading: useInitialLoading(data, loading, error),
      itemResaleRaffleUser,
      isError: false,
      submitting,
    },
    actions: {
      async onCancel() {
        return cancelResaleItemRaffle({
          variables: {
            inputs: {
              itemId,
              resaleRaffleId: raffleId,
            },
          },
        })
          .then(() => {
            router.push(
              generatePath(routerPages.ResaleRaffleDetailCancelCompletion, {
                raffleId,
                itemId,
              })
            )
          })
          .catch(e => {
            showError(e)
          })
      },
      onPay() {
        router.push(
          generatePath(routerPages.ResaleRaffleRepaymentConfirmation, {
            raffleId,
            itemId,
          })
        )
      },
    },
  }
})

const QUERY = gql`
  query ResaleRaffleDetail($raffleId: ID!, $itemId: String!, $skuId: String) {
    itemResaleRaffleUser(raffleId: $raffleId, itemId: $itemId) {
      ...ItemResaleRaffleUserDetail
    }
  }
  ${ItemResaleRaffleUserDetailFragmentDoc}
`

const CANCEL_RESALE_ITEM_RAFFLE = gql`
  mutation cancelResaleItemRaffle(
    $inputs: CancelResaleItemRaffleInput!
    $skuId: String
  ) {
    cancelResaleItemRaffle(inputs: $inputs) {
      ...ItemResaleRaffleUserDetail
    }
  }
  ${ItemResaleRaffleUserDetailFragmentDoc}
`
